import { useEffect, useState, type ReactNode } from 'react';
import { healthResponseSchema, type HealthResponse } from '@forecastify/shared';
import {
  AppShell,
  Avatar,
  Badge,
  Button,
  Checkbox,
  CommandDialog,
  DataGrid,
  DataList,
  DatePicker,
  Dialog,
  DropdownMenu,
  Icon,
  Kbd,
  Label,
  Progress,
  RangePicker,
  Select,
  Separator,
  Sheet,
  Switch,
  THEMES,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TextInput,
  Textarea,
  Toaster,
  Tooltip,
  applyTheme,
  type AppShellLabels,
  type CalendarLabels,
  type DataGridLabels,
  type DataGridRow,
  type DataListColumn,
  type DataListLabels,
  type DataListRow,
  type DateRange,
  type SortState,
  type RangePickerLabels,
  type Toast,
} from '@forecastify/ui';
import { apiGet } from '../api';

/**
 * C1's screen: **the primitives, in every theme.**
 *
 * It is a specimen sheet, not a design — the real shell arrives in C3. It exists so the token
 * layer can be looked at, because that is the only way to see the thing the automated checks
 * cannot: whether `bg-input` resolving to a real rule actually produces a visible border.
 * The guard in `packages/ui` proves the utility emits CSS; only an eye proves the CSS is any
 * good.
 *
 * ⚠️ The theme switcher is deliberately a plain `<select>` of all eight. The real picker with
 * its swatches is C6, and it should derive those swatches from the tokens rather than
 * hand-copying them — see `docs/plan/debt.md`.
 */
/**
 * ⚠️ **These live here because the components refuse to own them.** Every string the pickers
 * render is a prop (CLAUDE.md rule 7), so a specimen has to supply the whole Hungarian calendar.
 * When the i18n catalogue arrives these come out of it — and that is the point: the components
 * do not change, only where the words come from.
 *
 * `Sze` and `Szo`, not `Sz` twice. The design system's own `RangePicker` hardcodes `Sz` for both
 * *szerda* and *szombat*, which makes two columns of the week indistinguishable — see
 * `docs/plan/design-system-findings.md` I-4.
 */
const HU_CALENDAR: CalendarLabels = {
  months: [
    'január',
    'február',
    'március',
    'április',
    'május',
    'június',
    'július',
    'augusztus',
    'szeptember',
    'október',
    'november',
    'december',
  ],
  weekdays: ['H', 'K', 'Sze', 'Cs', 'P', 'Szo', 'V'],
  today: 'Ma',
  clear: 'Törlés',
  previousMonth: 'Előző hónap',
  previousPeriod: 'Előző időszak',
  nextPeriod: 'Következő időszak',
  zoomOut: 'Nagyítás kifelé',
  nextMonth: 'Következő hónap',
  placeholder: 'Válassz dátumot',
  dialogLabel: 'Dátumválasztó',
};

const HU_RANGE: RangePickerLabels = {
  ...HU_CALENDAR,
  apply: 'Alkalmaz',
  reset: 'Visszaállítás',
  trigger: 'Időszak',
  dialogLabel: 'Időszak választása',
};

/** The mockup's frozen today, so the specimen matches what the prototype draws. */
const TODAY = '2026-08-19';

/**
 * ⚠️ **C3's labels, and there are a lot of them on purpose.** Every word `DataList`, `DataGrid`
 * and `AppShell` render is a prop (CLAUDE.md rule 7), so a screen has to supply the whole
 * vocabulary. This is what the i18n catalogue will hold; the components do not change when it
 * arrives, only where the words come from.
 */
const LIST_LABELS: DataListLabels = {
  search: 'Keresés és szűrés…',
  filter: 'Szűrő',
  views: 'Nézet',
  columns: 'Oszlopok',
  clearAll: 'Szűrők törlése',
  clearFilter: (label) => `${label} szűrő törlése`,
  rangeFrom: 'Ettől',
  rangeTo: 'Eddig',
  switchView: 'Nézet váltása',
  table: 'Feladatok',
  selectAll: 'Összes kijelölése',
  selectRow: 'Sor kijelölése',
  selectedCount: (n) => `${String(n)} kijelölve`,
  clearSelection: 'Mégsem',
  rowMenu: 'Sor műveletek',
  sortBy: (label) => `Rendezés: ${label}`,
  loading: 'A rád osztott feladatok betöltése',
};

const GRID_LABELS: DataGridLabels = {
  grid: 'Becslések',
  readOnly: (label) => `A ${label} oszlop nem szerkeszthető.`,
  required: (label) => `A ${label} nem lehet üres.`,
};

const SHELL_LABELS: AppShellLabels = {
  navigation: 'Fő navigáció',
  search: 'Keresés…',
  searchHint: '⌘K',
  notifications: 'Értesítések',
  unreadCount: (n) => `${String(n)} olvasatlan`,
  markAllRead: 'Összes olvasottnak',
  noNotifications: 'Nincs értesítés.',
  workspaceMenu: 'Workspace váltása',
  userMenu: 'Fiók',
  skipToContent: 'Ugrás a tartalomra',
};

const LIST_COLUMNS: DataListColumn[] = [
  { key: 'k', label: 'Kulcs', width: 110, sortKey: 'key' },
  { key: 't', label: 'Feladat', sortKey: 'title' },
  { key: 'proj', label: 'Projekt', width: 170 },
  { key: 'st', label: 'Státusz', width: 130 },
  { key: 'as', label: 'Felelős', width: 120 },
  { key: 'due', label: 'Határidő', width: 110, align: 'right', sortKey: 'due_date' },
  { key: 'est', label: 'Becslés / tény', width: 170 },
];

/**
 * ⚠️ Cells carry a `sort` value in the mockup; they do not here, because this `DataList` never
 * sorts. The `sortKey` on the COLUMN is what the caller sends to the server.
 */
const LIST_ROWS: DataListRow[] = [
  {
    id: 'HSO-131',
    cells: {
      k: { text: 'HSO-131', mono: true, muted: true },
      t: { text: 'Ajánlatkérő űrlap validáció', bold: true },
      proj: { dot: 'var(--id-3)', text: 'Hosszú Sor', muted: true },
      st: { ring: 'var(--status-info)', fill: 'transparent', text: 'Folyamatban' },
      as: {
        avatar: { initials: 'TG', color: 'var(--id-4)', title: 'Takács Gábor' },
        text: 'Gábor',
        muted: true,
      },
      due: { text: 'aug 24.', mono: true },
      est: {
        text: '13:30 / 16:00',
        mono: true,
        muted: true,
        progress: { value: 84, color: 'var(--status-ok)', label: 'Készültség' },
      },
    },
  },
  {
    id: 'HSO-118',
    cells: {
      k: { text: 'HSO-118', mono: true, muted: true },
      t: { text: 'Számlázási export', bold: true },
      proj: { dot: 'var(--id-3)', text: 'Hosszú Sor', muted: true },
      st: { ring: 'var(--status-ok)', fill: 'var(--status-ok)', text: 'Kész' },
      as: {
        avatar: { initials: 'FA', color: 'var(--id-1)', title: 'Farkas András' },
        text: 'András',
        muted: true,
      },
      due: { text: 'aug 19.', mono: true, className: 'text-destructive' },
      est: {
        text: '8:00 / 8:00',
        mono: true,
        muted: true,
        progress: { value: 100, label: 'Készültség' },
      },
    },
  },
  {
    id: 'ROSS-1043',
    cells: {
      k: { text: 'ROSS-1043', mono: true, muted: true },
      t: { text: 'Kapacitás nézet', bold: true },
      proj: { dot: 'var(--id-6)', text: 'Rossmann', muted: true },
      st: { ring: 'var(--status-bad)', fill: 'transparent', text: 'Blokkolt' },
      as: {
        avatar: { initials: 'KB', color: 'var(--id-6)', title: 'Kovács Béla' },
        text: 'Béla',
        muted: true,
      },
      /* ⚠️ Absent, not zero — the em-dash case, visible on the page. */
      due: null,
      est: { text: '2:15 / –', mono: true, muted: true },
    },
  },
];

type ApiState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly health: HealthResponse }
  | { readonly kind: 'error'; readonly message: string };

export function Specimen(): ReactNode {
  const [state, setState] = useState<ApiState>({ kind: 'loading' });
  const [theme, setTheme] = useState('light');
  const [checked, setChecked] = useState(true);
  const [on, setOn] = useState(false);

  /* C2's surfaces. All controlled — the components own no open/closed state of their own except
   * the menus, which are attached to their own trigger. */
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [view, setView] = useState('list');
  const [section, setSection] = useState('overview');
  const [due, setDue] = useState<string | null>('2026-08-24');
  const [range, setRange] = useState<DateRange | null>({ from: '2026-08-01', to: '2026-08-31' });
  const [toasts, setToasts] = useState<Toast[]>([]);

  /* C3's state. Sort, search and filter live HERE — the list reports intent and the screen
   * decides, which over the API becomes a query rather than a comparator. */
  const [listSearch, setListSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [savedView, setSavedView] = useState('all');
  const [taskView, setTaskView] = useState('list');
  const [listSort, setListSort] = useState<readonly SortState[]>([]);
  const [notifications, setNotifications] = useState([
    {
      id: 'n1',
      title: 'HSO-131 rád került',
      description: 'Farkas András osztotta rád',
      time: 'ma 11:26',
      icon: 'user-round' as const,
      unread: true,
    },
    {
      id: 'n2',
      title: 'ROSS-1043 blokkolt',
      description: 'Két függőség sérül.',
      time: 'ma 09:14',
      icon: 'triangle-alert' as const,
      unread: true,
    },
    {
      id: 'n3',
      title: 'HSO-118 lezárva',
      time: 'tegnap 16:04',
      icon: 'circle-check' as const,
      unread: false,
    },
  ]);
  const [gridRows, setGridRows] = useState<DataGridRow[]>([
    { id: 'HSO-131', k: 'HSO-131', est: '16:00', rate: '22 000', note: 'Első kör' },
    { id: 'HSO-118', k: 'HSO-118', est: '8:00', rate: '22 000', note: null },
    { id: 'ROSS-1043', k: 'ROSS-1043', est: '4:00', rate: '19 500', note: 'Blokkolt' },
  ]);

  /* ⚠️ The auto-dismiss timer lives HERE, not in `Toaster`. The mockup's is 5.2s. It belongs
   * with the state that would also have to cancel it, because a component owning a timeout while
   * the caller owns the array gives one disappearance two sources of truth. */
  const push = (toast: Omit<Toast, 'id'>): void => {
    const id = `t${String(Date.now())}${String(Math.random())}`;
    setToasts((current) => [...current, { ...toast, id }]);
    setTimeout(() => {
      setToasts((current) => current.filter((t) => t.id !== id));
    }, 5200);
  };

  const dismiss = (id: string | number): void => {
    setToasts((current) => current.filter((t) => t.id !== id));
  };

  /* ⌘K, because a palette that only opens from a button is not a palette. */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    let cancelled = false;

    apiGet('/health')
      .then((body) => healthResponseSchema.parse(body))
      .then((health) => {
        if (!cancelled) setState({ kind: 'ready', health });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState({ kind: 'error', message: error instanceof Error ? error.message : 'unknown' });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /* The screen's own filtering — three rows, so a local predicate; over the API, a query. */
  const filtering = listSearch !== '' || statusFilter !== '';
  const visibleRows = LIST_ROWS.filter((row) => {
    /* The cast is the honest part: `cells` is `Record<string, Cell | string | number | null>`,
     * and this screen knows its own cells are objects with a `text`. A real screen filters on
     * the domain model, not on the rendered cell — this only exists because the specimen has no
     * model behind it. */
    const title = (row.cells.t as { text?: string } | null)?.text ?? '';
    const status = (row.cells.st as { text?: string } | null)?.text ?? '';
    if (listSearch !== '' && !`${row.id} ${title}`.toLowerCase().includes(listSearch.toLowerCase()))
      return false;
    return statusFilter === '' || status === statusFilter;
  });

  return (
    <AppShell
      labels={SHELL_LABELS}
      workspace={{
        name: 'LogiNet Delivery',
        initial: 'F',
        menu: [
          { label: 'Workspace beállítások', icon: 'settings' },
          { type: 'separator' },
          { label: 'Kilépés a workspace-ből', icon: 'log-out', destructive: true },
        ],
      }}
      user={{
        name: 'Takács Gábor',
        email: 'gabor.takacs@loginet.hu',
        initials: 'TG',
        color: 'var(--id-4)',
        menu: [
          { label: 'Fiókbeállítások', icon: 'user-round' },
          { label: 'Értesítések', icon: 'bell' },
        ],
      }}
      nav={[
        { label: 'Kezdőlap', icon: 'home', active: true, onSelect: () => undefined },
        { label: 'Feladataim', icon: 'list-checks', count: 7, onSelect: () => undefined },
        { label: 'Minden feladat', icon: 'kanban', count: 28, onSelect: () => undefined },
        { label: 'Időrögzítés', icon: 'clock', onSelect: () => undefined },
        { label: 'Agent', icon: 'sparkles', badge: 'preview', onSelect: () => undefined },
      ]}
      sections={[
        {
          label: 'Projektek',
          onSelectAll: () => undefined,
          allLabel: 'Minden projekt',
          onAdd: () => undefined,
          addLabel: 'Új projekt',
          entries: [
            { label: 'Hosszú Sor', color: 'var(--id-3)', count: 4, onSelect: () => undefined },
            { label: 'Rossmann', color: 'var(--id-6)', count: 11, onSelect: () => undefined },
            {
              label: 'Belső fejlesztés',
              color: 'var(--id-1)',
              count: 0,
              onSelect: () => undefined,
            },
          ],
        },
        {
          label: 'Workspace',
          entries: [
            { label: 'Ügyfelek', icon: 'building', count: 6, onSelect: () => undefined },
            { label: 'Dolgozók', icon: 'users', count: 9, onSelect: () => undefined },
          ],
        },
      ]}
      notifications={notifications}
      onMarkAllRead={() => {
        setNotifications((current) => current.map((n) => ({ ...n, unread: false })));
      }}
      onOpenSearch={() => {
        setPaletteOpen(true);
      }}
      header={<span className="text-[13.5px] font-medium">Mintalap · C1–C3</span>}
      headerActions={
        <span className="text-muted-foreground text-[12px]">
          {state.kind === 'ready' ? `API · ${state.health.status}` : null}
        </span>
      }
    >
      {/* ⚠️ The pane's OWN scroll region. `AppShell` is `h-screen overflow-hidden`, so a child
          that does not scroll is a child whose bottom half is unreachable. */}
      <div data-screen="specimen" className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl p-8">
          <header className="flex items-baseline gap-3">
            <h1 className="text-lg font-semibold">Forecastify</h1>
            {state.kind === 'loading' && (
              <span role="status" className="text-[12px] text-muted-foreground">
                Csatlakozás az API-hoz…
              </span>
            )}
            {state.kind === 'ready' && (
              <span
                data-api-status={state.health.status}
                className="text-[12px] text-muted-foreground"
              >
                Az API válaszol · {state.health.status} · {state.health.version}
              </span>
            )}
            {state.kind === 'error' && (
              <span data-api-status="unreachable" className="text-[12px] text-destructive">
                Az API nem válaszol – {state.message}
              </span>
            )}
          </header>

          <div className="mt-6 flex items-center gap-3">
            <Label htmlFor="theme">Téma</Label>
            <Select
              id="theme"
              value={theme}
              /* ⚠️ The ids, not translated labels. `packages/ui` stopped carrying Hungarian strings
                 when i18n landed (rule 7), and this is a development specimen with no `I18nProvider`
                 above it — so it shows what it has, which is the theme's id. The real gallery with
                 translated names is the Theme tab in account settings. */
              options={THEMES.map((t) => ({ value: t.id, label: t.id }))}
              onChange={(event) => {
                setTheme(event.target.value);
              }}
            />
            <span className="text-[12px] text-muted-foreground">
              {THEMES.find((t) => t.id === theme)?.dark === true ? 'dark' : 'light'}
            </span>
          </div>

          <Separator className="my-6" />

          <section className="rounded-lg border bg-card p-5 text-card-foreground">
            <h2 className="text-[13px] font-medium">Gombok</h2>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button>Elsődleges</Button>
              <Button variant="secondary">Másodlagos</Button>
              <Button variant="outline">Körvonal</Button>
              <Button variant="ghost">Szellem</Button>
              <Button variant="destructive">Törlés</Button>
              <Button variant="link">Hivatkozás</Button>
              <Button size="sm">Kicsi</Button>
              <Button disabled>Letiltva</Button>
            </div>

            <h2 className="mt-6 text-[13px] font-medium">Jelölések</h2>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Badge>Aktív</Badge>
              <Badge variant="secondary">Tervezet</Badge>
              <Badge variant="destructive">Késésben</Badge>
              <Badge variant="outline">Archivált</Badge>
              <Avatar fallback="TG" color="#5E6AD2" title="Takács Gábor" />
              <Avatar fallback="FA" color="#26A879" title="Farkas András" size={28} />
              <Kbd>⌘K</Kbd>
            </div>

            <h2 className="mt-6 text-[13px] font-medium">Beviteli mezők</h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="name">Feladat neve</Label>
                <TextInput id="name" placeholder="Mi a kész állapot?" />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="who">Felelős</Label>
                <Select
                  id="who"
                  options={[
                    { value: 'TG', label: 'Takács Gábor' },
                    { value: 'FA', label: 'Farkas András' },
                  ]}
                />
              </div>
              <div className="sm:col-span-2 flex flex-col gap-1.5">
                <Label htmlFor="desc">Leírás</Label>
                <Textarea id="desc" placeholder="Részletek…" />
              </div>
            </div>

            <h2 className="mt-6 text-[13px] font-medium">Váltók</h2>
            <div className="mt-3 flex flex-wrap items-center gap-5">
              <span className="flex items-center gap-2">
                <Checkbox checked={checked} onCheckedChange={setChecked} label="Számlázható" />
                <span className="text-[12.5px]">Számlázható</span>
              </span>
              <span className="flex items-center gap-2">
                <Switch checked={on} onCheckedChange={setOn} label="Értesítések e-mailben" />
                <span className="text-[12.5px]">Értesítések e-mailben</span>
              </span>
              <span className="flex items-center gap-2">
                <Checkbox checked disabled label="Zárolt" />
                <span className="text-[12.5px] text-muted-foreground">Zárolt</span>
              </span>
            </div>

            <h2 className="mt-6 text-[13px] font-medium">Készültség</h2>
            <div className="mt-3 flex flex-col gap-2">
              <Progress value={64} label="Készültség" />
              <Progress value={112} label="Becslés túllépve" color="var(--destructive)" />
            </div>
          </section>

          <section className="mt-6 rounded-lg border bg-card p-5 text-card-foreground">
            <h2 className="text-[13px] font-medium">Fülek</h2>
            <div className="mt-3 flex flex-col gap-4">
              <Tabs
                label="Feladatnézet"
                value={view}
                onValueChange={setView}
                items={[
                  { value: 'list', label: 'Lista', icon: 'list-checks', count: 28 },
                  { value: 'board', label: 'Tábla', icon: 'kanban' },
                  { value: 'timeline', label: 'Idővonal', icon: 'chart-column' },
                  { value: 'grid', label: 'Adatrács', icon: 'layout-grid' },
                ]}
              />
              <Tabs
                variant="underline"
                label="Időrögzítés"
                value={section}
                onValueChange={setSection}
                items={[
                  { value: 'overview', label: 'Áttekintés' },
                  { value: 'logs', label: 'Időlogok', count: 19 },
                  /* A count of zero renders as zero — `state-rules.md` §5. */
                  { value: 'blocked', label: 'Blokkolt', count: 0 },
                ]}
              />
            </div>

            <h2 className="mt-6 text-[13px] font-medium">Menük</h2>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <DropdownMenu
                label="Sor műveletek"
                emptyLabel="Nincs találat"
                items={[
                  { label: 'Megnyitás', icon: 'arrow-right', hint: 'HSO-131' },
                  { label: 'Szerkesztés', icon: 'pencil' },
                  { label: 'Duplikálás', icon: 'copy' },
                  { type: 'separator' },
                  { label: 'Sablonból', icon: 'file-text', disabled: true },
                  { label: 'Törlés', icon: 'trash', destructive: true },
                ]}
              >
                <Button variant="ghost" size="icon" aria-label="További műveletek">
                  <Icon name="ellipsis" size={16} />
                </Button>
              </DropdownMenu>

              <DropdownMenu
                label="Felelős"
                width={240}
                searchPlaceholder="Keresés…"
                emptyLabel="Nincs ilyen dolgozó"
                items={[
                  { type: 'label', label: 'Projekt tagjai' },
                  { label: 'Takács Gábor', hint: 'TG', dot: 'var(--id-4)', checked: true },
                  { label: 'Farkas András', hint: 'FA', dot: 'var(--id-1)', checked: false },
                  { label: 'Kovács Béla', hint: 'KB', dot: 'var(--id-6)', checked: false },
                  { label: 'Nagy Emese', hint: 'NE', dot: 'var(--id-3)', checked: false },
                ]}
              >
                <Button variant="outline" size="sm">
                  <Icon name="user-round" size={14} />
                  Felelős
                  <Icon name="chevron-down" size={13} className="opacity-60" />
                </Button>
              </DropdownMenu>

              <DropdownMenu
                label="Státusz"
                items={[
                  { label: 'Új', description: 'Még nincs elkezdve' },
                  { label: 'Folyamatban', description: 'Valaki dolgozik rajta' },
                  { label: 'Blokkolt', description: 'Külső függőségre vár' },
                ]}
              >
                <Button variant="outline" size="sm">
                  Státusz
                </Button>
              </DropdownMenu>

              <Tooltip content="Heti kapacitás: 40:00">
                <span className="text-[12.5px] tabular-nums underline decoration-dotted">
                  32:00
                </span>
              </Tooltip>
            </div>

            <h2 className="mt-6 text-[13px] font-medium">Dátumok</h2>
            <div className="mt-3 flex flex-wrap items-end gap-3">
              <div className="flex w-[220px] flex-col gap-1.5">
                <Label htmlFor="due">Határidő</Label>
                <DatePicker value={due} onChange={setDue} today={TODAY} labels={HU_CALENDAR} />
              </div>
              <RangePicker
                value={range}
                onChange={setRange}
                today={TODAY}
                labels={HU_RANGE}
                defaultRange={{ from: '2026-08-01', to: '2026-08-31' }}
                presets={[
                  { label: 'Ez a hét', from: '2026-08-17', to: '2026-08-23' },
                  { label: 'Ez a hónap', from: '2026-08-01', to: '2026-08-31' },
                  { label: 'Előző hónap', from: '2026-07-01', to: '2026-07-31' },
                ]}
              />
              <span className="text-[12px] text-muted-foreground">
                {range === null ? '–' : `${range.from} – ${range.to}`}
              </span>
            </div>

            <h2 className="mt-6 text-[13px] font-medium">Táblázat</h2>
            <div className="mt-3 overflow-hidden rounded-md border">
              <Table label="Rögzített idő">
                <TableHeader>
                  <TableRow>
                    <TableHead>Feladat</TableHead>
                    <TableHead>Felelős</TableHead>
                    <TableHead className="text-right">Rögzített</TableHead>
                    <TableHead className="text-right">Becslés</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[
                    { k: 'HSO-131', who: 'Takács Gábor', logged: '13:30', est: '16:00' },
                    { k: 'HSO-118', who: 'Farkas András', logged: '8:00', est: '8:00' },
                    /* An em-dash, not a zero: an unestimated task's remaining budget is
                     * uncomputable rather than nil — `state-rules.md` §5. */
                    { k: 'ROSS-1043', who: 'Kovács Béla', logged: '2:15', est: '–' },
                  ].map((row) => (
                    <TableRow key={row.k}>
                      <TableCell>{row.k}</TableCell>
                      <TableCell>{row.who}</TableCell>
                      <TableCell numeric>{row.logged}</TableCell>
                      <TableCell numeric>{row.est}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <h2 className="mt-6 text-[13px] font-medium">Felületek</h2>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                onClick={() => {
                  setDialogOpen(true);
                }}
              >
                <Icon name="plus" size={14} />
                Idő rögzítése
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSheetOpen(true);
                }}
              >
                <Icon name="panel-right" size={14} />
                Feladat megnyitása
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setPaletteOpen(true);
                }}
              >
                <Icon name="command" size={14} />
                Paletta
                <Kbd>⌘K</Kbd>
              </Button>
            </div>

            <h2 className="mt-6 text-[13px] font-medium">Visszajelzés</h2>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  push({
                    title: '3 feladat áthelyezve',
                    description: 'HSO-131, HSO-118, ROSS-1043 → Folyamatban',
                    variant: 'success',
                  });
                }}
              >
                Sikeres
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  push({
                    title: 'HSO-131 törölve',
                    description: 'A rögzített 13:30 megmarad az archívumban.',
                    variant: 'success',
                    action: {
                      label: 'Visszavonás',
                      onAction: async () => {
                        await new Promise((resolve) => setTimeout(resolve, 600));
                      },
                      failureLabel: 'A visszavonás nem sikerült.',
                    },
                  });
                }}
              >
                Visszavonható
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  push({
                    title: 'ROSS-1043 törölve',
                    variant: 'success',
                    action: {
                      label: 'Visszavonás',
                      /* ⚠️ Deliberately rejects. `state-rules.md` §7: undo is a reverse mutation and
                       * can itself fail, and a failed undo says so. This is the only way to look at
                       * that state, and it is the one the mockup cannot produce at all. */
                      onAction: async () => {
                        await new Promise((resolve) => setTimeout(resolve, 600));
                        throw new Error('409');
                      },
                      failureLabel: 'A visszavonás nem sikerült – a feladatot közben módosították.',
                    },
                  });
                }}
              >
                Bukó visszavonás
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  push({
                    title: 'Nagyobbnak kell lennie 0-nál.',
                    description: 'A becslés nem lehet negatív.',
                    variant: 'error',
                  });
                }}
              >
                Hiba
              </Button>
            </div>
          </section>

          <Dialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            title="Idő rögzítése"
            description="Elfogad 8, 8h, 8:00 vagy 8,0 alakot – H:MM alakban jelenik meg."
            closeLabel="Bezárás"
            width={440}
            footer={
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setDialogOpen(false);
                  }}
                >
                  Mégse
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    setDialogOpen(false);
                    push({
                      title: '8:00 rögzítve',
                      description: 'HSO-131 · aug 19.',
                      variant: 'success',
                    });
                  }}
                >
                  Mentés
                </Button>
              </>
            }
          >
            <div className="grid gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="hours">Idő</Label>
                <TextInput id="hours" placeholder="8, 8h, 8:00 vagy 8,0" />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="when">Nap</Label>
                <DatePicker value={TODAY} today={TODAY} labels={HU_CALENDAR} clearable={false} />
              </div>
            </div>
          </Dialog>

          <Sheet
            open={sheetOpen}
            onOpenChange={setSheetOpen}
            title="HSO-131 · Ajánlatkérő űrlap validáció"
            closeLabel="Bezárás"
            headerLeft={<Icon name="circle-dot" size={15} className="text-status-info" />}
            actions={[
              { label: 'Modal nézet', icon: 'square-dashed', onSelect: () => undefined },
              { label: 'Teljes képernyő', icon: 'maximize', onSelect: () => undefined },
            ]}
          >
            <div className="grid gap-4 p-4">
              <p className="text-[13px] leading-relaxed">
                A jobb oldali lap a termék elsődleges részletnézete – nem külön oldal. A fejléc 48px
                és nem gördül; a törzs egyetlen gördülő terület.
              </p>
              <Separator />
              <dl className="grid grid-cols-[120px_1fr] gap-y-2 text-[12.5px]">
                <dt className="text-muted-foreground">Felelős</dt>
                <dd className="flex items-center gap-2">
                  <Avatar fallback="TG" color="var(--id-4)" size={20} title="Takács Gábor" />
                  Takács Gábor
                </dd>
                <dt className="text-muted-foreground">Határidő</dt>
                <dd className="tabular-nums">2026-08-24</dd>
                <dt className="text-muted-foreground">Rögzített</dt>
                <dd className="tabular-nums">13:30 / 16:00</dd>
                <dt className="text-muted-foreground">Csúszás</dt>
                <dd className="tabular-nums">+4n</dd>
              </dl>
              <Progress value={84} label="Készültség" color="var(--status-ok)" />
              <p className="text-[12px] text-muted-foreground">
                Nyomj Escape-et – csak ez a lap záródik, a mögötte lévő semmi.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="w-fit"
                onClick={() => {
                  setDialogOpen(true);
                }}
              >
                Idő rögzítése innen
              </Button>
            </div>
          </Sheet>

          <CommandDialog
            open={paletteOpen}
            onOpenChange={setPaletteOpen}
            label="Parancspaletta"
            placeholder="Ugrás vagy parancs…"
            emptyLabel={(query) => `Nincs találat erre: „${query}”`}
            items={[
              { group: 'Navigáció', label: 'Kezdőlap', icon: 'home' },
              { group: 'Navigáció', label: 'Feladataim', icon: 'list-checks' },
              { group: 'Navigáció', label: 'Minden feladat', icon: 'kanban' },
              { group: 'Navigáció', label: 'Heti timesheet', icon: 'calendar-days' },
              { group: 'Navigáció', label: 'Projektek', icon: 'folder' },
              { group: 'Navigáció', label: 'Riportok', icon: 'chart-column' },
              { group: 'Nézet', label: 'Idővonal (Gantt)', icon: 'chart-column' },
              { group: 'Nézet', label: 'Adatrács', icon: 'layout-grid' },
              {
                group: 'Művelet',
                label: 'Új feladat',
                icon: 'plus',
                hint: 'C',
                onSelect: () => {
                  setDialogOpen(true);
                },
              },
              {
                group: 'Feladatok',
                label: 'Ajánlatkérő űrlap validáció',
                hint: 'HSO-131',
                icon: 'circle-dot',
              },
              {
                group: 'Feladatok',
                label: 'Számlázási export',
                hint: 'ROSS-1043',
                icon: 'circle-dot',
              },
            ]}
          />

          <Toaster toasts={toasts} onDismiss={dismiss} label="Értesítések" dismissLabel="Bezárás" />

          <section className="mt-6 rounded-lg border bg-card text-card-foreground">
            <h2 className="border-b px-5 py-3 text-[13px] font-medium">Lista</h2>
            {/* ⚠️ A bounded height, because `DataList` is `flex-1` over its own scroll region — it is
            built to fill a pane, and a specimen sheet has no pane to fill. */}
            <div className="flex h-[420px] flex-col">
              <DataList
                labels={LIST_LABELS}
                columns={LIST_COLUMNS}
                rows={visibleRows}
                search={listSearch}
                onSearchChange={setListSearch}
                filters={[
                  {
                    key: 'st',
                    label: 'Státusz',
                    value: statusFilter,
                    options: [
                      { value: '', label: 'Mind' },
                      { value: 'Folyamatban', label: 'Folyamatban' },
                      { value: 'Kész', label: 'Kész' },
                      { value: 'Blokkolt', label: 'Blokkolt' },
                    ],
                    onChange: setStatusFilter,
                  },
                ]}
                views={[
                  { value: 'all', label: 'Nyitott feladatok', count: LIST_ROWS.length },
                  { value: 'mine', label: 'Rám osztva', count: 1 },
                  /* A saved view with nothing in it still shows its zero. */
                  { value: 'late', label: 'Csúszó', count: 0 },
                ]}
                view={savedView}
                onViewChange={setSavedView}
                sort={listSort}
                /**
                 * ⚠️ **The screen sorts, not the component.** Over three fixed rows that is a local
                 * comparator; over the API it becomes a query parameter, and `DataList` cannot tell
                 * the difference. That is the whole point of `onSortChange` — see CLAUDE.md §3.
                 */
                onSortChange={setListSort}
                viewSwitch={{
                  value: taskView,
                  onChange: setTaskView,
                  items: [
                    { value: 'list', label: 'Lista', icon: 'list-checks' },
                    { value: 'board', label: 'Tábla', icon: 'kanban' },
                    { value: 'timeline', label: 'Idővonal', icon: 'chart-column' },
                  ],
                }}
                actions={[
                  {
                    label: 'Új feladat',
                    icon: 'plus',
                    variant: 'default',
                    onSelect: () => {
                      setDialogOpen(true);
                    },
                  },
                ]}
                rowMenu={(id) => [
                  {
                    label: 'Megnyitás',
                    icon: 'arrow-right',
                    hint: id,
                    onSelect: () => {
                      setSheetOpen(true);
                    },
                  },
                  { label: 'Időrögzítés erre', icon: 'timer' },
                  { type: 'separator' },
                  { label: 'Lezárás', icon: 'circle-check' },
                  { label: 'Rám osztás', icon: 'user-round' },
                  { type: 'separator' },
                  {
                    label: 'Törlés',
                    icon: 'trash',
                    destructive: true,
                    onSelect: () => {
                      push({
                        title: `${id} törölve`,
                        variant: 'success',
                        action: {
                          label: 'Visszavonás',
                          onAction: async () => {
                            await new Promise((resolve) => setTimeout(resolve, 400));
                          },
                          failureLabel: 'A visszavonás nem sikerült.',
                        },
                      });
                    },
                  },
                ]}
                bulkActions={[
                  {
                    label: 'Lezárás',
                    icon: 'circle-check',
                    onSelect: (ids) => {
                      push({
                        title: `${String(ids.length)} feladat lezárva`,
                        description: 'Státusz: Kész.',
                        variant: 'success',
                      });
                    },
                  },
                  {
                    label: 'Törlés',
                    icon: 'trash',
                    destructive: true,
                    onSelect: (ids) => {
                      push({
                        title: `${String(ids.length)} feladat törölve`,
                        description: ids.join(', '),
                        variant: 'success',
                        action: {
                          label: 'Visszavonás',
                          onAction: async () => {
                            await new Promise((resolve) => setTimeout(resolve, 400));
                          },
                          failureLabel: 'A visszavonás nem sikerült.',
                        },
                      });
                    },
                  },
                ]}
                onRowClick={() => {
                  setSheetOpen(true);
                }}
                /**
                 * ⚠️ `state-rules.md` §4 in one prop: the copy distinguishes "nothing is wrong" from
                 * "your filter hid everything", and the second SAYS HOW MANY rows are hidden. The
                 * component cannot know which — only the screen knows whether a filter is on.
                 */
                empty={
                  filtering
                    ? {
                        icon: 'funnel',
                        title: 'A szűrő mindent elrejtett',
                        body: `${String(LIST_ROWS.length - visibleRows.length)} feladat esik ki a szűrő miatt. Töröld a szűrőt, vagy írj be mást.`,
                        actionLabel: 'Szűrő törlése',
                        onAction: () => {
                          setListSearch('');
                          setStatusFilter('');
                        },
                      }
                    : {
                        icon: 'list-checks',
                        title: 'Nincs feladat ebben a nézetben',
                        body: 'Vegyél fel egyet, hogy legyen mit ütemezni.',
                        actionLabel: 'Új feladat',
                        onAction: () => {
                          setDialogOpen(true);
                        },
                      }
                }
                footerNote={`${String(visibleRows.length)} feladat${filtering ? ' · szűrve' : ''} · összesen 24:00 becslés`}
              />
            </div>
          </section>

          <section className="mt-6 rounded-lg border bg-card text-card-foreground">
            <h2 className="border-b px-5 py-3 text-[13px] font-medium">Adatrács</h2>
            <div className="flex h-[220px] flex-col">
              <DataGrid
                labels={GRID_LABELS}
                columns={[
                  { key: 'k', label: 'Kulcs', width: 110, mono: true, readOnly: true },
                  { key: 'est', label: 'Becslés', width: 110, mono: true, required: true },
                  { key: 'rate', label: 'Óradíj', width: 110, mono: true },
                  { key: 'note', label: 'Megjegyzés', width: 260 },
                ]}
                rows={gridRows}
                onChange={(id, key, value) => {
                  setGridRows((current) =>
                    current.map((row) => (row.id === id ? { ...row, [key]: value } : row)),
                  );
                }}
                onCopy={(text) => {
                  push({ title: 'Vágólapra másolva', description: text, variant: 'info' });
                }}
                /* ⚠️ A refused edit states the constraint. It never just declines. */
                onBlocked={(reason) => {
                  push({ title: reason, variant: 'error' });
                }}
              />
            </div>
            <p className="text-muted-foreground border-t px-5 py-2 text-[11.5px]">
              Nyílakkal lépkedj, Enter vagy F2 szerkeszt, gépelés azonnal ír, Esc elvet, ⌘/Ctrl+C
              másol, ⌘/Ctrl+V beilleszt, ⌘/Ctrl+D lefelé tölt. A Kulcs csak olvasható, a Becslés
              kötelező.
            </p>
          </section>

          <p className="mt-6 text-[12px] text-muted-foreground">
            Ez a lap a C1–C3 mintalapja – a tokenek, a primitívek, az átfedő felületek, a lista és
            az adatrács, mind a nyolc témában. A tényleges képernyők a C6-tól jönnek.
          </p>
        </div>
      </div>
    </AppShell>
  );
}
